import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, generatePath } from 'react-router-dom';
import { ImageIcon, PlayCircleIcon } from '@phosphor-icons/react';
import { Checkbox, CheckboxVariant, Badge } from '@/components';
import { TableColumn } from '@/types/table';
import { FullAlertRule } from '@/types/alert';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { formatRelativeTimeI18n } from '@/utils/date';
import { useAuthUser } from '@/hooks/useAuthUser';
import { UserAvatar } from '@/components/NavigationBar';
import { AppRoutes } from '@/constants/routes';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@/components/ui/Tooltip';
import { PromptAttachment } from '@/types/prompt';

const COLUMN_WIDTHS = {
    CHECKBOX: '56px',
    PROMPT_MULTISELECT: '216px',
    PROMPT_DEFAULT: '240px',
    STATUS: '88px',
    LAST_TRIGGERED: '180px',
    CREATED_BY_MIN: '140px',
    PROMPT_MAX_WIDTH: '300px',
} as const;

const EMPTY_VALUE = '-';

function StatusBadge({ isActive }: { isActive: boolean }) {
    const { t } = useTranslation(I18nNamespace.ALERT_RULES);

    return (
        <Badge
            value={isActive ? t('status.active') : t('status.inactive')}
            variant={isActive ? 'green' : 'base'}
        />
    );
}

function LastTriggeredCell({
    lastAlert,
}: {
    lastAlert?: { id: string; createdAt: number };
}) {
    if (!lastAlert) {
        return <span className="text-sm text-grey-300">{EMPTY_VALUE}</span>;
    }

    return (
        <Link
            to={generatePath(AppRoutes.EVENT, { eventId: lastAlert.id })}
            className="inline-flex max-w-full min-w-0 items-center gap-1.5 px-2 py-1 rounded-md transition-colors hover:bg-grey-500"
            onClick={(e) => e.stopPropagation()}
        >
            <PlayCircleIcon
                className="size-4 shrink-0 text-grey-300"
                weight="fill"
            />
            <span className="min-w-0 truncate text-sm text-white-100">
                {formatRelativeTimeI18n(lastAlert.createdAt)}
            </span>
        </Link>
    );
}

function CreatedByCell({
    userName,
    unknownLabel,
    createdAt,
}: {
    userName?: string;
    unknownLabel: string;
    createdAt?: number;
}) {
    const displayName = userName || unknownLabel;
    const dateText = createdAt
        ? formatRelativeTimeI18n(createdAt)
        : EMPTY_VALUE;

    return (
        <div className="flex items-center gap-3">
            <UserAvatar name={displayName} size="sm" />
            <div className="flex flex-col justify-center min-w-0">
                <span className="text-sm text-white-100 truncate">
                    {displayName}
                </span>
                <span className="text-sm text-grey-300 truncate">
                    {dateText}
                </span>
            </div>
        </div>
    );
}

function CheckboxCell({ isSelected }: { isSelected: boolean }) {
    return <Checkbox checked={isSelected} variant={CheckboxVariant.WHITE} />;
}

function PromptCell({
    text,
    attachments,
}: {
    text?: string;
    attachments: Array<Pick<PromptAttachment, 'id' | 'type' | 'url'>>;
}) {
    const hasAttachments = attachments?.length > 0;
    if (!text && !hasAttachments) {
        return <span className="text-sm text-grey-300">{EMPTY_VALUE}</span>;
    }

    return (
        <div
            className="relative"
            style={{
                maxWidth: COLUMN_WIDTHS.PROMPT_MAX_WIDTH,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
            }}
        >
            {text && (
                <Tooltip>
                    <TooltipTrigger asChild>
                        <span className="text-sm text-white-100 line-clamp-2 whitespace-normal break-words pr-5">
                            {text}
                        </span>
                    </TooltipTrigger>
                    <TooltipContent
                        side="top"
                        sideOffset={8}
                        className="max-w-[400px] whitespace-pre-wrap break-words"
                    >
                        {text}
                    </TooltipContent>
                </Tooltip>
            )}
            <div className="flex flex-row gap-1">
                {hasAttachments &&
                    attachments.map((attachment) => (
                        <div key={attachment.id} className="px-0.5">
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <div>
                                        <ImageIcon
                                            size={16}
                                            alt={attachment.url
                                                .split('/')
                                                .pop()}
                                        />
                                    </div>
                                </TooltipTrigger>

                                <TooltipContent
                                    side="top"
                                    sideOffset={8}
                                    variant="panel"
                                    className="p-1"
                                >
                                    <img
                                        src={attachment.url}
                                        alt={attachment.url.split('/').pop()}
                                        className="max-w-[200px] max-h-[140px] object-contain rounded"
                                    />
                                </TooltipContent>
                            </Tooltip>
                        </div>
                    ))}
            </div>
        </div>
    );
}

interface UseAlertRulesColumnsOptions {
    isMultiSelectMode?: boolean;
    selectedIds?: Set<string>;
}

export function useAlertRulesColumns(
    options: UseAlertRulesColumnsOptions = {},
): TableColumn<FullAlertRule>[] {
    const { t } = useTranslation([
        I18nNamespace.ALERT_RULES,
        I18nNamespace.COMMON,
    ]);
    const { isMultiSelectMode = false, selectedIds = new Set() } = options;
    const { user } = useAuthUser();

    return useMemo(() => {
        const columns: TableColumn<FullAlertRule>[] = [];

        if (isMultiSelectMode) {
            columns.push({
                id: 'checkbox',
                header: '',
                cell: ({ row }) => (
                    <CheckboxCell
                        isSelected={selectedIds.has(row.original.id)}
                    />
                ),
                width: COLUMN_WIDTHS.CHECKBOX,
            });
        }

        columns.push(
            {
                id: 'prompt',
                header: t('columns.prompt'),
                cell: ({ row }) => {
                    const prompt = row.original.prompts?.[0];
                    const hasAttachment =
                        (prompt?.attachments?.length ?? 0) > 0;
                    return (
                        <PromptCell
                            text={prompt?.text}
                            attachments={prompt?.attachments}
                        />
                    );
                },
                minWidth: isMultiSelectMode
                    ? COLUMN_WIDTHS.PROMPT_MULTISELECT
                    : COLUMN_WIDTHS.PROMPT_DEFAULT,
            },
            {
                id: 'status',
                header: t('columns.status'),
                cell: ({ row }) => (
                    <StatusBadge isActive={row.original.active} />
                ),
                width: COLUMN_WIDTHS.STATUS,
            },
            {
                id: 'lastTriggered',
                header: t('columns.lastTriggered'),
                cell: ({ row }) => (
                    <LastTriggeredCell lastAlert={row.original.lastAlert} />
                ),
                width: COLUMN_WIDTHS.LAST_TRIGGERED,
                interactive: true,
            },
            {
                id: 'createdBy',
                header: t('columns.createdBy'),
                cell: ({ row }) => (
                    <CreatedByCell
                        userName={user?.name}
                        unknownLabel={t('common:unknown')}
                        createdAt={row.original.createdAt}
                    />
                ),
                minWidth: COLUMN_WIDTHS.CREATED_BY_MIN,
            },
        );

        return columns;
    }, [t, isMultiSelectMode, selectedIds, user]);
}
